import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog'
import { useAuth } from '../../context/AuthContext'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Trophy,
  Briefcase,
  X,
  CheckCircle2,
  Clock,
  Building2,
  Sparkles,
  Users,
  Save,
  Send,
  Loader2,
  Lock,
  HelpCircle,
  AlertCircle
} from 'lucide-react'
import api from '../../services/api'
import { validateEmail, validatePhone, validateRequired } from '../../utils/validation'
import CountryCodeSelect from '../../components/common/CountryCodeSelect'
import { getPOCData, validatePresenterVsPOC } from '../../utils/pocStorage'

export default function Benefits() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [activeModal, setActiveModal] = useState(null)
  const [companyName, setCompanyName] = useState('')
  const [olympiadData, setOlympiadData] = useState({
    member1: {
      firstName: '',
      lastName: '',
      email: '',
      countryCode: '91',
      mobileNumber: '',
      designation: '',
      companyName: ''
    },
    member2: {
      firstName: '',
      lastName: '',
      email: '',
      countryCode: '91',
      mobileNumber: '',
      designation: '',
      companyName: ''
    }
  })
  const [loading, setLoading] = useState(false)
  const [savingOlympiad, setSavingOlympiad] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isFormLocked, setIsFormLocked] = useState(false)
  const [validationErrors, setValidationErrors] = useState({
    member1: {},
    member2: {}
  })

  // POC data and toggle states
  const [pocData, setPocData] = useState(null)
  const [fillMember1WithPOC, setFillMember1WithPOC] = useState(false)
  const [fillMember2WithPOC, setFillMember2WithPOC] = useState(false)
  const [showMixedDataDialog, setShowMixedDataDialog] = useState(false)
  const [mixedDataConflicts, setMixedDataConflicts] = useState({ member: null, matching: [], nonMatching: [] })

  // Fetch existing benefits data when modal opens and cleanup when closes
  useEffect(() => {
    if (activeModal === 'olympiad') {
      fetchBenefitsData()
      fetchCompanyName()
    } else {
      // Reset toggle states when modal closes
      setFillMember1WithPOC(false)
      setFillMember2WithPOC(false)
    }
  }, [activeModal])

  // Auto-open modal if navigated from another page with state
  useEffect(() => {
    if (location.state?.openModal) {
      setActiveModal(location.state.openModal)
      // Clear the state to prevent re-opening on refresh
      window.history.replaceState({}, document.title)
    }
  }, [location])

  // Handle Escape key press to close modals
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && activeModal) {
        setActiveModal(null)
      }
    }

    document.addEventListener('keydown', handleEscapeKey)
    return () => document.removeEventListener('keydown', handleEscapeKey)
  }, [activeModal])

  const fetchCompanyName = async () => {
    try {
      const response = await api.get('/company/info')
      if (response.data && response.data.company) {
        const name = response.data.company.companyName
        setCompanyName(name)
        // Prefill company name for both members if not already set
        setOlympiadData(prev => ({
          ...prev,
          member1: {
            ...prev.member1,
            companyName: prev.member1.companyName || name
          },
          member2: {
            ...prev.member2,
            companyName: prev.member2.companyName || name
          }
        }))
      }
    } catch (error) {
      console.error('Error fetching company info:', error)
    }
  }

  const fetchBenefitsData = async () => {
    setLoading(true)
    try {
      // Use the new FO nominations endpoint
      const response = await api.get('/company/benefits/fo-nominations')
      if (response.data && response.data.olympiadTeam) {
        const team = response.data.olympiadTeam
        // Map the data to the new structure, handling backward compatibility
        setOlympiadData({
          member1: {
            firstName: team.member1?.firstName || '',
            lastName: team.member1?.lastName || '',
            email: team.member1?.email || '',
            countryCode: team.member1?.countryCode?.replace('+', '') || '91',
            mobileNumber: team.member1?.mobileNumber || team.member1?.whatsapp || '',
            designation: team.member1?.designation || team.member1?.title || '',
            companyName: team.member1?.companyName || ''
          },
          member2: {
            firstName: team.member2?.firstName || '',
            lastName: team.member2?.lastName || '',
            email: team.member2?.email || '',
            countryCode: team.member2?.countryCode?.replace('+', '') || '91',
            mobileNumber: team.member2?.mobileNumber || team.member2?.whatsapp || '',
            designation: team.member2?.designation || team.member2?.title || '',
            companyName: team.member2?.companyName || ''
          }
        })

        // If API also provided companyName, use it immediately to ensure it's never empty on first open
        if (response.data.companyName) {
          const apiCompanyName = response.data.companyName
          setCompanyName((prev) => prev || apiCompanyName)
          setOlympiadData(prev => ({
            ...prev,
            member1: { ...prev.member1, companyName: prev.member1.companyName || apiCompanyName },
            member2: { ...prev.member2, companyName: prev.member2.companyName || apiCompanyName }
          }))
        }

        // Load POC data from API response or sessionStorage
        const pocDataFromAPI = response.data.pocData
        const pocDataFromStorage = getPOCData()
        const loadedPocData = pocDataFromAPI || pocDataFromStorage
        if (loadedPocData) {
          setPocData(loadedPocData)
        }

        // Track if data was previously submitted and lock form
        if (team.submitted) {
          setIsSubmitted(true)
          setIsFormLocked(true)
        }
      }
    } catch (error) {
      console.error('Error fetching FO nominations:', error)
      // If error, still allow user to fill the form
    } finally {
      setLoading(false)
    }
  }

  const handleOlympiadChange = (member, field, value) => {
    setOlympiadData(prev => ({
      ...prev,
      [member]: {
        ...prev[member],
        [field]: value
      }
    }))
  }

  // Helpers for duplicate detection across members
  const normalizeEmail = (str) => (str || '').trim().toLowerCase()
  const normalizePhone = (phone) => (phone || '').replace(/\D/g, '')
  const DUPLICATE_EMAIL_MSG = 'Email cannot be the same for both members'
  const DUPLICATE_PHONE_MSG = 'Mobile number cannot be the same for both members'

  const updateDuplicateErrors = (nextData) => {
    const m1Email = normalizeEmail(nextData?.member1?.email)
    const m2Email = normalizeEmail(nextData?.member2?.email)
    const emailDup = !!(m1Email && m2Email && m1Email === m2Email)

    const m1Phone = normalizePhone(nextData?.member1?.mobileNumber)
    const m2Phone = normalizePhone(nextData?.member2?.mobileNumber)
    const phoneDup = !!(m1Phone && m2Phone && m1Phone === m2Phone)

    setValidationErrors((prev) => {
      const next = {
        member1: { ...prev.member1 },
        member2: { ...prev.member2 }
      }

      // Email duplicates
      if (emailDup) {
        next.member1.email = DUPLICATE_EMAIL_MSG
        next.member2.email = DUPLICATE_EMAIL_MSG
      } else {
        if (next.member1.email === DUPLICATE_EMAIL_MSG) next.member1.email = ''
        if (next.member2.email === DUPLICATE_EMAIL_MSG) next.member2.email = ''
      }

      // Phone duplicates
      if (phoneDup) {
        next.member1.mobileNumber = DUPLICATE_PHONE_MSG
        next.member2.mobileNumber = DUPLICATE_PHONE_MSG
      } else {
        if (next.member1.mobileNumber === DUPLICATE_PHONE_MSG) next.member1.mobileNumber = ''
        if (next.member2.mobileNumber === DUPLICATE_PHONE_MSG) next.member2.mobileNumber = ''
      }

      return next
    })
  }

  // Live duplicate checking when relevant fields change
  useEffect(() => {
    updateDuplicateErrors(olympiadData)
  }, [
    olympiadData.member1.email,
    olympiadData.member1.mobileNumber,
    olympiadData.member2.email,
    olympiadData.member2.mobileNumber
  ])

  // Handle "I am Attending" toggle for Member 1
  const handleFillMember1WithPOC = (checked) => {
    setFillMember1WithPOC(checked)

    if (checked && pocData) {
      // Auto-fill member1 data with POC information
      setOlympiadData(prev => ({
        ...prev,
        member1: {
          ...prev.member1,
          firstName: pocData.pocFirstName || '',
          lastName: pocData.pocLastName || '',
          email: pocData.pocEmail || '',
          countryCode: pocData.pocCountryCode?.replace('+', '') || '91',
          mobileNumber: pocData.pocPhone || '',
          companyName: prev.member1.companyName || companyName
        }
      }))

      // Clear any validation errors for locked fields
      setValidationErrors(prev => ({
        ...prev,
        member1: { ...prev.member1, firstName: '', lastName: '', email: '', mobileNumber: '', companyName: '' }
      }))
    } else if (!checked) {
      // When unchecked, clear only the POC-specific auto-filled fields
      setOlympiadData(prev => ({
        ...prev,
        member1: {
          ...prev.member1,
          firstName: '',
          lastName: '',
          email: '',
          countryCode: '91',
          mobileNumber: ''
        }
      }))

      // Clear validation errors for the cleared fields
      setValidationErrors(prev => ({
        ...prev,
        member1: { ...prev.member1, firstName: '', lastName: '', email: '', mobileNumber: '' }
      }))
    }
  }

  // Handle "I am Attending" toggle for Member 2
  const handleFillMember2WithPOC = (checked) => {
    setFillMember2WithPOC(checked)

    if (checked && pocData) {
      // Auto-fill member2 data with POC information
      setOlympiadData(prev => ({
        ...prev,
        member2: {
          ...prev.member2,
          firstName: pocData.pocFirstName || '',
          lastName: pocData.pocLastName || '',
          email: pocData.pocEmail || '',
          countryCode: pocData.pocCountryCode?.replace('+', '') || '91',
          mobileNumber: pocData.pocPhone || '',
          companyName: prev.member2.companyName || companyName
        }
      }))

      // Clear any validation errors for locked fields
      setValidationErrors(prev => ({
        ...prev,
        member2: { ...prev.member2, firstName: '', lastName: '', email: '', mobileNumber: '', companyName: '' }
      }))
    } else if (!checked) {
      // When unchecked, clear only the POC-specific auto-filled fields
      setOlympiadData(prev => ({
        ...prev,
        member2: {
          ...prev.member2,
          firstName: '',
          lastName: '',
          email: '',
          countryCode: '91',
          mobileNumber: ''
        }
      }))

      // Clear validation errors for the cleared fields
      setValidationErrors(prev => ({
        ...prev,
        member2: { ...prev.member2, firstName: '', lastName: '', email: '', mobileNumber: '' }
      }))
    }
  }

  // Handle navigate to help page
  const handleGoToHelp = () => {
    setActiveModal(null) // Close the modal first
    navigate('/company/help')
  }

  // Handle confirmation dialog - Yes button
  const handleConfirmYes = () => {
    const memberNum = mixedDataConflicts.member
    if (!memberNum) return

    setShowMixedDataDialog(false)

    // Check if the OTHER member's toggle is already enabled
    // If so, just clear the conflicting fields instead of enabling this toggle
    if (memberNum === 'member1') {
      if (fillMember2WithPOC) {
        // Member2 toggle is already on, so just clear member1's conflicting fields
        const allConflicts = [...(mixedDataConflicts.matching || []), ...(mixedDataConflicts.nonMatching || [])]
        setOlympiadData(prev => {
          const clearedData = { ...prev.member1 }
          if (allConflicts.includes('name')) {
            clearedData.firstName = ''
            clearedData.lastName = ''
          }
          if (allConflicts.includes('email')) {
            clearedData.email = ''
          }
          if (allConflicts.includes('phone')) {
            clearedData.mobileNumber = ''
            clearedData.countryCode = '91'
          }
          return { ...prev, member1: clearedData }
        })
      } else {
        // No other toggle is on, enable member1 toggle and fill with POC data
        setFillMember1WithPOC(true)
        if (pocData) {
          setOlympiadData(prev => ({
            ...prev,
            member1: {
              ...prev.member1,
              firstName: pocData.pocFirstName || '',
              lastName: pocData.pocLastName || '',
              email: pocData.pocEmail || '',
              countryCode: pocData.pocCountryCode?.replace('+', '') || '91',
              mobileNumber: pocData.pocPhone || '',
              companyName: prev.member1.companyName || companyName
            }
          }))

          // Clear validation errors for member1 locked fields
          setValidationErrors(prev => ({
            ...prev,
            member1: { ...prev.member1, firstName: '', lastName: '', email: '', mobileNumber: '', companyName: '' }
          }))
        }
      }
    } else if (memberNum === 'member2') {
      if (fillMember1WithPOC) {
        // Member1 toggle is already on, so just clear member2's conflicting fields
        const allConflicts = [...(mixedDataConflicts.matching || []), ...(mixedDataConflicts.nonMatching || [])]
        setOlympiadData(prev => {
          const clearedData = { ...prev.member2 }
          if (allConflicts.includes('name')) {
            clearedData.firstName = ''
            clearedData.lastName = ''
          }
          if (allConflicts.includes('email')) {
            clearedData.email = ''
          }
          if (allConflicts.includes('phone')) {
            clearedData.mobileNumber = ''
            clearedData.countryCode = '91'
          }
          return { ...prev, member2: clearedData }
        })
      } else {
        // No other toggle is on, enable member2 toggle and fill with POC data
        setFillMember2WithPOC(true)
        if (pocData) {
          setOlympiadData(prev => ({
            ...prev,
            member2: {
              ...prev.member2,
              firstName: pocData.pocFirstName || '',
              lastName: pocData.pocLastName || '',
              email: pocData.pocEmail || '',
              countryCode: pocData.pocCountryCode?.replace('+', '') || '91',
              mobileNumber: pocData.pocPhone || '',
              companyName: prev.member2.companyName || companyName
            }
          }))

          // Clear validation errors for member2 locked fields
          setValidationErrors(prev => ({
            ...prev,
            member2: { ...prev.member2, firstName: '', lastName: '', email: '', mobileNumber: '', companyName: '' }
          }))
        }
      }
    }
  }

  // Handle confirmation dialog - Edit button
  const handleConfirmEdit = () => {
    const memberNum = mixedDataConflicts.member
    if (!memberNum) return

    // Clear the conflicting fields
    const allConflicts = [...(mixedDataConflicts.matching || []), ...(mixedDataConflicts.nonMatching || [])]

    if (memberNum === 'member1') {
      setOlympiadData(prev => {
        const clearedData = { ...prev.member1 }
        if (allConflicts.includes('name')) {
          clearedData.firstName = ''
          clearedData.lastName = ''
        }
        if (allConflicts.includes('email')) {
          clearedData.email = ''
        }
        if (allConflicts.includes('phone')) {
          clearedData.mobileNumber = ''
          clearedData.countryCode = '91'
        }
        return { ...prev, member1: clearedData }
      })
    } else if (memberNum === 'member2') {
      setOlympiadData(prev => {
        const clearedData = { ...prev.member2 }
        if (allConflicts.includes('name')) {
          clearedData.firstName = ''
          clearedData.lastName = ''
        }
        if (allConflicts.includes('email')) {
          clearedData.email = ''
        }
        if (allConflicts.includes('phone')) {
          clearedData.mobileNumber = ''
          clearedData.countryCode = '91'
        }
        return { ...prev, member2: clearedData }
      })
    }

    setShowMixedDataDialog(false)
  }

  const saveOlympiadTeam = async () => {
    setSavingOlympiad(true)
    try {
      // Add + prefix to country codes when saving
      const dataToSave = {
        member1: {
          ...olympiadData.member1,
          countryCode: '+' + olympiadData.member1.countryCode
        },
        member2: {
          ...olympiadData.member2,
          countryCode: olympiadData.member2.countryCode ? '+' + olympiadData.member2.countryCode : ''
        }
      }

      await api.put('/company/benefits/olympiad', {
        olympiadTeam: dataToSave
      })
      showToast('Team members saved successfully!')
    } catch (error) {
      console.error('Error saving team members:', error)
      showToast('Failed to save team members', 'error')
    } finally {
      setSavingOlympiad(false)
    }
  }

  const submitOlympiadNominations = async () => {
    const errors = { member1: {}, member2: {} }

    // Validate Member 1 (Required)
    const m1FirstNameValidation = validateRequired(olympiadData.member1.firstName, 'First name')
    if (!m1FirstNameValidation.isValid) errors.member1.firstName = m1FirstNameValidation.error

    const m1LastNameValidation = validateRequired(olympiadData.member1.lastName, 'Last name')
    if (!m1LastNameValidation.isValid) errors.member1.lastName = m1LastNameValidation.error

    const m1EmailValidation = validateEmail(olympiadData.member1.email)
    if (!m1EmailValidation.isValid) errors.member1.email = m1EmailValidation.error

    const m1PhoneValidation = validatePhone(olympiadData.member1.mobileNumber)
    if (!m1PhoneValidation.isValid) errors.member1.mobileNumber = m1PhoneValidation.error

    const m1DesignationValidation = validateRequired(olympiadData.member1.designation, 'Designation')
    if (!m1DesignationValidation.isValid) errors.member1.designation = m1DesignationValidation.error

    const m1CompanyValidation = validateRequired(olympiadData.member1.companyName, 'Company name')
    if (!m1CompanyValidation.isValid) errors.member1.companyName = m1CompanyValidation.error

    // Validate Member 2 (Optional - only if any field is filled)
    const hasMember2Data = olympiadData.member2.firstName || olympiadData.member2.lastName ||
      olympiadData.member2.email || olympiadData.member2.mobileNumber

    if (hasMember2Data) {
      if (olympiadData.member2.email) {
        const m2EmailValidation = validateEmail(olympiadData.member2.email)
        if (!m2EmailValidation.isValid) errors.member2.email = m2EmailValidation.error
      }

      if (olympiadData.member2.mobileNumber) {
        const m2PhoneValidation = validatePhone(olympiadData.member2.mobileNumber)
        if (!m2PhoneValidation.isValid) errors.member2.mobileNumber = m2PhoneValidation.error
      }
    }

    // Cross-member duplicate checks (email and phone)
    const email1 = normalizeEmail(olympiadData.member1.email)
    const email2 = normalizeEmail(olympiadData.member2.email)
    const phone1 = normalizePhone(olympiadData.member1.mobileNumber)
    const phone2 = normalizePhone(olympiadData.member2.mobileNumber)

    if (email1 && email2 && email1 === email2) {
      errors.member1.email = DUPLICATE_EMAIL_MSG
      errors.member2.email = DUPLICATE_EMAIL_MSG
    }

    if (phone1 && phone2 && phone1 === phone2) {
      errors.member1.mobileNumber = DUPLICATE_PHONE_MSG
      errors.member2.mobileNumber = DUPLICATE_PHONE_MSG
    }

    // Check if there are any errors
    const hasErrors = Object.keys(errors.member1).length > 0 || Object.keys(errors.member2).length > 0
    if (hasErrors) {
      setValidationErrors(errors)
      // showToast('Please fix all validation errors before submitting', 'error')
      // Focus first duplicate error if applicable, else first field error
      if (errors.member1?.email === DUPLICATE_EMAIL_MSG || errors.member2?.email === DUPLICATE_EMAIL_MSG) {
        const el = document.getElementById('member1-email') || document.getElementById('member2-email')
        if (el && typeof el.focus === 'function') el.focus()
      } else if (errors.member1?.mobileNumber === DUPLICATE_PHONE_MSG || errors.member2?.mobileNumber === DUPLICATE_PHONE_MSG) {
        const el = document.getElementById('member1-mobile') || document.getElementById('member2-mobile')
        if (el && typeof el.focus === 'function') el.focus()
      } else if (errors.member1?.firstName) {
        const el = document.getElementById('member1-firstName')
        if (el && typeof el.focus === 'function') el.focus()
      } else if (errors.member1?.lastName) {
        const el = document.getElementById('member1-lastName')
        if (el && typeof el.focus === 'function') el.focus()
      } else if (errors.member1?.email) {
        const el = document.getElementById('member1-email')
        if (el && typeof el.focus === 'function') el.focus()
      } else if (errors.member1?.mobileNumber) {
        const el = document.getElementById('member1-mobile')
        if (el && typeof el.focus === 'function') el.focus()
      } else if (errors.member1?.designation) {
        const el = document.getElementById('member1-designation')
        if (el && typeof el.focus === 'function') el.focus()
      } else if (errors.member1?.companyName) {
        const el = document.getElementById('member1-company')
        if (el && typeof el.focus === 'function') el.focus()
      } else if (errors.member2?.email) {
        const el = document.getElementById('member2-email')
        if (el && typeof el.focus === 'function') el.focus()
      } else if (errors.member2?.mobileNumber) {
        const el = document.getElementById('member2-mobile')
        if (el && typeof el.focus === 'function') el.focus()
      }
      return
    }

    // Validate member1 against POC data for mixed data issues
    if (pocData) {
      const member1POCValidation = validatePresenterVsPOC({
        presenterFirstName: olympiadData.member1.firstName,
        presenterLastName: olympiadData.member1.lastName,
        presenterEmail: olympiadData.member1.email,
        presenterMobile: olympiadData.member1.mobileNumber
      }, pocData)

      if (!member1POCValidation.isValid) {
        // Mixed data detected for member1
        const normalize = (str) => (str || '').trim().toLowerCase()
        const normalizePhone = (phone) => (phone || '').replace(/\D/g, '')

        const m1FullName = normalize(`${olympiadData.member1.firstName} ${olympiadData.member1.lastName}`)
        const pocFullName = normalize(`${pocData.pocFirstName} ${pocData.pocLastName}`)
        const nameMatches = m1FullName === pocFullName

        const m1Email = normalize(olympiadData.member1.email)
        const pocEmail = normalize(pocData.pocEmail)
        const emailMatches = m1Email === pocEmail

        const m1Phone = normalizePhone(olympiadData.member1.mobileNumber)
        const pocPhone = normalizePhone(pocData.pocPhone)
        const phoneMatches = m1Phone === pocPhone

        const matchingFields = []
        const nonMatchingFields = []

        if (nameMatches) matchingFields.push('name')
        else nonMatchingFields.push('name')

        if (emailMatches) matchingFields.push('email')
        else nonMatchingFields.push('email')

        if (phoneMatches) matchingFields.push('phone')
        else nonMatchingFields.push('phone')

        setMixedDataConflicts({
          member: 'member1',
          matching: matchingFields,
          nonMatching: nonMatchingFields
        })
        setShowMixedDataDialog(true)
        return
      }

      // Validate member2 against POC data if member2 has data
      if (hasMember2Data) {
        const member2POCValidation = validatePresenterVsPOC({
          presenterFirstName: olympiadData.member2.firstName,
          presenterLastName: olympiadData.member2.lastName,
          presenterEmail: olympiadData.member2.email,
          presenterMobile: olympiadData.member2.mobileNumber
        }, pocData)

        if (!member2POCValidation.isValid) {
          // Mixed data detected for member2
          const normalize = (str) => (str || '').trim().toLowerCase()
          const normalizePhone = (phone) => (phone || '').replace(/\D/g, '')

          const m2FullName = normalize(`${olympiadData.member2.firstName} ${olympiadData.member2.lastName}`)
          const pocFullName = normalize(`${pocData.pocFirstName} ${pocData.pocLastName}`)
          const nameMatches = m2FullName === pocFullName

          const m2Email = normalize(olympiadData.member2.email)
          const pocEmail = normalize(pocData.pocEmail)
          const emailMatches = m2Email === pocEmail

          const m2Phone = normalizePhone(olympiadData.member2.mobileNumber)
          const pocPhone = normalizePhone(pocData.pocPhone)
          const phoneMatches = m2Phone === pocPhone

          const matchingFields = []
          const nonMatchingFields = []

          if (nameMatches) matchingFields.push('name')
          else nonMatchingFields.push('name')

          if (emailMatches) matchingFields.push('email')
          else nonMatchingFields.push('email')

          if (phoneMatches) matchingFields.push('phone')
          else nonMatchingFields.push('phone')

          setMixedDataConflicts({
            member: 'member2',
            matching: matchingFields,
            nonMatching: nonMatchingFields
          })
          setShowMixedDataDialog(true)
          return
        }
      }
    }

    // Clear validation errors
    setValidationErrors({ member1: {}, member2: {} })

    // Format country code with + prefix
    const dataToSubmit = {
      ...olympiadData,
      member1: {
        ...olympiadData.member1,
        countryCode: '+' + olympiadData.member1.countryCode
      },
      member2: olympiadData.member2.firstName ? {
        ...olympiadData.member2,
        countryCode: '+' + olympiadData.member2.countryCode
      } : olympiadData.member2
    }

    setSavingOlympiad(true)
    try {
      await api.post('/company/benefits/olympiad/submit', {
        olympiadTeam: dataToSubmit
      })
      showToast('Nominations submitted successfully!')
      setActiveModal(null)
    } catch (error) {
      console.error('Error submitting nominations:', error)
      showToast('Failed to submit nominations', 'error')
    } finally {
      setSavingOlympiad(false)
    }
  }

  const showToast = (message, type = 'success') => {
    const toast = document.createElement('div')
    toast.className = `fixed top-20 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg z-[60] ${type === 'success' ? 'bg-green-600' : 'bg-red-600'
      } text-white font-medium`
    toast.textContent = message
    document.body.appendChild(toast)
    setTimeout(() => {
      toast.remove()
    }, 3000)
  }

  const closeModal = () => {
    setActiveModal(null)
  }
  const benefits = [
    {
      id: 'olympiad',
      title: 'Fintech Olympiad',
      subtitle: 'Competition Entry',
      description: 'Participate in the Prestigious Fintech Olympiad and represent your company at the National Level!',
      icon: Trophy,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      cardBg: 'bg-gradient-to-br from-green-50/50 to-emerald-50/30',
      borderColor: 'border-green-200',
      actionText: 'Learn More',
      action: () => setActiveModal('olympiad')
    },
    {
      id: 'jobPosting',
      title: 'Job Posting Platform',
      subtitle: 'Talent Acquisition',
      description: 'Access to exclusive job posting platform to find top fintech talent.',
      icon: Briefcase,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      cardBg: 'bg-gradient-to-br from-blue-50/50 to-indigo-50/30',
      borderColor: 'border-blue-200',
      actionText: 'Post Jobs',
      action: () => setActiveModal('jobPosting')
    }
  ]

  return (
    <div className="h-full overflow-hidden bg-gray-50 flex flex-col">
      <div className="flex-1 flex flex-col max-w-7xl mx-auto w-full px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex-shrink-0">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Available Benefits</h1>
          <p className="text-gray-600">Exclusive perks and opportunities for IFTA demo companies</p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 flex-1 content-start">
          {benefits.map((benefit) => {
            const Icon = benefit.icon
            return (
              <div
                key={benefit.id}
                className={`${benefit.cardBg} rounded-2xl border ${benefit.borderColor} p-6 hover:shadow-lg transition-all duration-200 hover:scale-[1.02]`}
              >
                {/* Icon and Title */}
                <div className="flex items-start gap-4 mb-4">
                  <div className={`${benefit.iconBg} p-3 rounded-xl`}>
                    <Icon className={`h-6 w-6 ${benefit.iconColor}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-1">
                      {benefit.title}
                    </h3>
                    <p className="text-sm text-gray-500">{benefit.subtitle}</p>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {benefit.description}
                </p>

                {/* Action Button */}
                <button
                  onClick={benefit.action}
                  className="w-full py-3 px-4 rounded-xl bg-white/70 backdrop-blur-sm border border-gray-200 text-gray-700 font-medium hover:bg-white hover:shadow-sm transition-all duration-200"
                >
                  {benefit.actionText}
                </button>
              </div>
            )
          })}
        </div>
      </div>

      {/* Fintech Olympiad Modal */}
      {activeModal === 'olympiad' && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setActiveModal(null)
            }
          }}
        >
          <div
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-r from-green-50 to-emerald-50 border-b px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-500 text-white rounded-xl">
                  <Trophy className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold">Fintech Olympiad</h2>
                  <p className="text-sm text-gray-600">Annual competition with exclusive benefits</p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {/* Form Lock Banner */}
                  {isFormLocked && (
                    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 mb-6 flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1">
                        <Lock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="font-semibold text-blue-900 mb-1">
                            Nominations Submitted
                          </h4>
                          <p className="text-sm text-blue-800">
                          Your Fintech Olympiad nominations have been submitted. If you need to make any changes, please contact our support team for assistance.
                          </p>
                        </div>
                      </div>
                      <Button
                        onClick={handleGoToHelp}
                        variant="outline"
                        size="sm"
                        className="flex-shrink-0 border-blue-300 hover:bg-blue-100 hover:border-blue-400 text-blue-700"
                      >
                        <HelpCircle className="h-4 w-4 mr-2" />
                        Help
                      </Button>
                    </div>
                  )}

                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
                    <p className="text-green-900">
                      Participate in the Prestigious Fintech Olympiad and represent your company at the National Level!
                    </p>
                    <p className="text-green-900">
                      To learn more, visit <a href="https://fintecholympiad.org" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-800 underline font-semibold">Fintech Olympiad</a>.
                    </p>
                    {/* <div className="flex items-center gap-2 mt-3 text-amber-700">
                      <Clock className="h-5 w-5" />
                      <span className="font-medium">Due on October 25, 2025</span>
                    </div> */}
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      What's in it for you?
                    </h3>
                    <div className="grid gap-3 md:grid-cols-2 font-semibold">
                      {[
                        'Get National Media PR and Visibility',
                        'Free Passes to Attend IFTA 2025',
                        'Participate in a Fun, Gamified Quiz',
                        'Win from a Prize Pool worth ₹5,00,000'
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                          <Sparkles className="h-4 w-4 text-green-500" />
                          <span className="text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Team Nomination Form */}
                  <div className="bg-white rounded-xl border-2 border-green-200 p-6">
                    <h3 className="text-xl font-semibold mb-2 flex items-center gap-2">
                      <Users className="h-6 w-6 text-green-600" />
                      Nominate Team Members
                      {isSubmitted && (
                        <span className="ml-auto text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                          Previously Submitted
                        </span>
                      )}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {isSubmitted
                        ? 'Review or update your previously submitted nominations.'
                        : 'Nominate up to 2 team members to participate in the Fintech Olympiad competition.'
                      }
                    </p>

                    <div className="space-y-6">
                      {/* Team Member 1 */}
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <h4 className="font-semibold mb-4 text-green-900">Team Member 1 (Required)</h4>

                        {/* I am Attending Toggle */}
                        {!isFormLocked && pocData && !fillMember2WithPOC && (
                          <div className="flex items-start gap-2 mb-4 p-3 bg-white rounded-lg border border-green-300">
                            <input
                              type="checkbox"
                              id="fillMember1WithPOC"
                              checked={fillMember1WithPOC}
                              onChange={(e) => handleFillMember1WithPOC(e.target.checked)}
                              className="mt-1 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            <label htmlFor="fillMember1WithPOC" className="text-sm font-medium text-gray-700 cursor-pointer flex-1">
                              I am attending
                            </label>
                          </div>
                        )}

                        {/* Company Name (Editable but prefilled) */}
                        <div className="mb-4">
                          <Label htmlFor="member1-company">Company Name *</Label>
                          <Input
                            id="member1-company"
                            placeholder="Enter company name"
                            value={olympiadData.member1.companyName}
                            onChange={(e) => {
                              handleOlympiadChange('member1', 'companyName', e.target.value)
                              if (validationErrors.member1.companyName) {
                                setValidationErrors(prev => ({
                                  ...prev,
                                  member1: { ...prev.member1, companyName: '' }
                                }))
                              }
                            }}
                            disabled={isFormLocked || fillMember1WithPOC}
                            className={`${validationErrors.member1.companyName && !(isFormLocked || fillMember1WithPOC) ? 'border-red-500' : ''} ${isFormLocked || fillMember1WithPOC ? 'bg-gray-100' : ''}`}
                          />
                          {validationErrors.member1.companyName && !(isFormLocked || fillMember1WithPOC) && (
                            <p className="text-sm text-red-500 mt-1">{validationErrors.member1.companyName}</p>
                          )}
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <Label htmlFor="member1-firstName">First Name *</Label>
                            <Input
                              id="member1-firstName"
                              placeholder="Enter first name"
                              value={olympiadData.member1.firstName}
                              onChange={(e) => {
                                handleOlympiadChange('member1', 'firstName', e.target.value)
                                if (validationErrors.member1.firstName) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    member1: { ...prev.member1, firstName: '' }
                                  }))
                                }
                              }}
                              disabled={isFormLocked || fillMember1WithPOC}
                              className={`${validationErrors.member1.firstName && !(isFormLocked || fillMember1WithPOC) ? 'border-red-500' : ''} ${isFormLocked || fillMember1WithPOC ? 'bg-gray-100' : ''}`}
                            />
                            {validationErrors.member1.firstName && !(isFormLocked || fillMember1WithPOC) && (
                              <p className="text-sm text-red-500 mt-1">{validationErrors.member1.firstName}</p>
                            )}
                          </div>
                          <div>
                            <Label htmlFor="member1-lastName">Last Name *</Label>
                            <Input
                              id="member1-lastName"
                              placeholder="Enter last name"
                              value={olympiadData.member1.lastName}
                              onChange={(e) => {
                                handleOlympiadChange('member1', 'lastName', e.target.value)
                                if (validationErrors.member1.lastName) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    member1: { ...prev.member1, lastName: '' }
                                  }))
                                }
                              }}
                              disabled={isFormLocked || fillMember1WithPOC}
                              className={`${validationErrors.member1.lastName && !(isFormLocked || fillMember1WithPOC) ? 'border-red-500' : ''} ${isFormLocked || fillMember1WithPOC ? 'bg-gray-100' : ''}`}
                            />
                            {validationErrors.member1.lastName && !(isFormLocked || fillMember1WithPOC) && (
                              <p className="text-sm text-red-500 mt-1">{validationErrors.member1.lastName}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2 mt-4">
                          <div>
                            <Label htmlFor="member1-email">Email Address *</Label>
                            <Input
                              id="member1-email"
                              type="email"
                              placeholder="Enter email address"
                              value={olympiadData.member1.email}
                              onChange={(e) => {
                                handleOlympiadChange('member1', 'email', e.target.value)
                                // Clear error when user types
                                if (validationErrors.member1.email) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    member1: { ...prev.member1, email: '' }
                                  }))
                                }
                              }}
                              onBlur={(e) => {
                                // Skip validation if field is locked
                                if (isFormLocked || fillMember1WithPOC) return

                                // Validate on blur
                                const validation = validateEmail(e.target.value)
                                if (!validation.isValid) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    member1: { ...prev.member1, email: validation.error }
                                  }))
                                }
                              }}
                              disabled={isFormLocked || fillMember1WithPOC}
                              className={`${validationErrors.member1.email && !(isFormLocked || fillMember1WithPOC) ? 'border-red-500' : ''} ${isFormLocked || fillMember1WithPOC ? 'bg-gray-100' : ''}`}
                            />
                            {validationErrors.member1.email && !(isFormLocked || fillMember1WithPOC) && (
                              <p className="text-sm text-red-500 mt-1">{validationErrors.member1.email}</p>
                            )}
                          </div>
                          <div>
                            <Label htmlFor="member1-designation">Designation *</Label>
                            <Input
                              id="member1-designation"
                              placeholder="e.g., CEO, CTO, Founder"
                              value={olympiadData.member1.designation}
                              onChange={(e) => {
                                handleOlympiadChange('member1', 'designation', e.target.value)
                                if (validationErrors.member1.designation) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    member1: { ...prev.member1, designation: '' }
                                  }))
                                }
                              }}
                              disabled={isFormLocked}
                              className={`${validationErrors.member1.designation && !isFormLocked ? 'border-red-500' : ''} ${isFormLocked ? 'bg-gray-100' : ''}`}
                            />
                            {validationErrors.member1.designation && !isFormLocked && (
                              <p className="text-sm text-red-500 mt-1">{validationErrors.member1.designation}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2 mt-4">
                          <div>
                            <Label htmlFor="member1-countryCode">Country Code *</Label>
                            <CountryCodeSelect
                              value={olympiadData.member1.countryCode}
                              onChange={(code) => handleOlympiadChange('member1', 'countryCode', code.replace('+', ''))}
                              disabled={isFormLocked || fillMember1WithPOC}
                            />
                          </div>
                          <div>
                            <Label htmlFor="member1-mobile">Mobile Number *</Label>
                            <Input
                              id="member1-mobile"
                              type="tel"
                              placeholder="Enter mobile number"
                              value={olympiadData.member1.mobileNumber}
                              onChange={(e) => {
                                handleOlympiadChange('member1', 'mobileNumber', e.target.value.replace(/\D/g, ''))
                                // Clear error when user types
                                if (validationErrors.member1.mobileNumber) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    member1: { ...prev.member1, mobileNumber: '' }
                                  }))
                                }
                              }}
                              onBlur={(e) => {
                                // Skip validation if field is locked
                                if (isFormLocked || fillMember1WithPOC) return

                                // Validate on blur
                                const validation = validatePhone(e.target.value)
                                if (!validation.isValid) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    member1: { ...prev.member1, mobileNumber: validation.error }
                                  }))
                                }
                              }}
                              disabled={isFormLocked || fillMember1WithPOC}
                              className={`${validationErrors.member1.mobileNumber && !(isFormLocked || fillMember1WithPOC) ? 'border-red-500' : ''} ${isFormLocked || fillMember1WithPOC ? 'bg-gray-100' : ''}`}
                            />
                            {validationErrors.member1.mobileNumber && !(isFormLocked || fillMember1WithPOC) && (
                              <p className="text-sm text-red-500 mt-1">{validationErrors.member1.mobileNumber}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              Enter mobile number (7-15 digits)
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Team Member 2 */}
                      <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                        <h4 className="font-semibold mb-4 text-emerald-900">Team Member 2 (Optional)</h4>

                        {/* I am Attending Toggle */}
                        {!isFormLocked && pocData && !fillMember1WithPOC && (
                          <div className="flex items-start gap-2 mb-4 p-3 bg-white rounded-lg border border-emerald-300">
                            <input
                              type="checkbox"
                              id="fillMember2WithPOC"
                              checked={fillMember2WithPOC}
                              onChange={(e) => handleFillMember2WithPOC(e.target.checked)}
                              className="mt-1 h-4 w-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                            />
                            <label htmlFor="fillMember2WithPOC" className="text-sm font-medium text-gray-700 cursor-pointer flex-1">
                              I am attending
                            </label>
                          </div>
                        )}

                        {/* Company Name (Editable but prefilled) */}
                        <div className="mb-4">
                          <Label htmlFor="member2-company">Company Name</Label>
                          <Input
                            id="member2-company"
                            placeholder="Enter company name"
                            value={olympiadData.member2.companyName}
                            onChange={(e) => handleOlympiadChange('member2', 'companyName', e.target.value)}
                            disabled={isFormLocked || fillMember2WithPOC}
                            className={isFormLocked || fillMember2WithPOC ? 'bg-gray-100' : ''}
                          />
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <Label htmlFor="member2-firstName">First Name</Label>
                            <Input
                              id="member2-firstName"
                              placeholder="Enter first name"
                              value={olympiadData.member2.firstName}
                              onChange={(e) => handleOlympiadChange('member2', 'firstName', e.target.value)}
                              disabled={isFormLocked || fillMember2WithPOC}
                              className={isFormLocked || fillMember2WithPOC ? 'bg-gray-100' : ''}
                            />
                          </div>
                          <div>
                            <Label htmlFor="member2-lastName">Last Name</Label>
                            <Input
                              id="member2-lastName"
                              placeholder="Enter last name"
                              value={olympiadData.member2.lastName}
                              onChange={(e) => handleOlympiadChange('member2', 'lastName', e.target.value)}
                              disabled={isFormLocked || fillMember2WithPOC}
                              className={isFormLocked || fillMember2WithPOC ? 'bg-gray-100' : ''}
                            />
                          </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2 mt-4">
                          <div>
                            <Label htmlFor="member2-email">Email Address</Label>
                            <Input
                              id="member2-email"
                              type="email"
                              placeholder="Enter email address"
                              value={olympiadData.member2.email}
                              onChange={(e) => {
                                handleOlympiadChange('member2', 'email', e.target.value)
                                // Clear error when user types
                                if (validationErrors.member2.email) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    member2: { ...prev.member2, email: '' }
                                  }))
                                }
                              }}
                              onBlur={(e) => {
                                // Skip validation if field is locked
                                if (isFormLocked || fillMember2WithPOC) return

                                // Validate only if field has content
                                if (e.target.value) {
                                  const validation = validateEmail(e.target.value)
                                  if (!validation.isValid) {
                                    setValidationErrors(prev => ({
                                      ...prev,
                                      member2: { ...prev.member2, email: validation.error }
                                    }))
                                  }
                                }
                              }}
                              disabled={isFormLocked || fillMember2WithPOC}
                              className={`${validationErrors.member2.email && !(isFormLocked || fillMember2WithPOC) ? 'border-red-500' : ''} ${isFormLocked || fillMember2WithPOC ? 'bg-gray-100' : ''}`}
                            />
                            {validationErrors.member2.email && !(isFormLocked || fillMember2WithPOC) && (
                              <p className="text-sm text-red-500 mt-1">{validationErrors.member2.email}</p>
                            )}
                          </div>
                          <div>
                            <Label htmlFor="member2-designation">Designation</Label>
                            <Input
                              id="member2-designation"
                              placeholder="e.g., CEO, CTO, Founder"
                              value={olympiadData.member2.designation}
                              onChange={(e) => handleOlympiadChange('member2', 'designation', e.target.value)}
                              disabled={isFormLocked}
                              className={isFormLocked ? 'bg-gray-100' : ''}
                            />
                          </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2 mt-4">
                          <div>
                            <Label htmlFor="member2-countryCode">Country Code</Label>
                            <CountryCodeSelect
                              value={olympiadData.member2.countryCode}
                              onChange={(code) => handleOlympiadChange('member2', 'countryCode', code.replace('+', ''))}
                              disabled={isFormLocked || fillMember2WithPOC}
                            />
                          </div>
                          <div>
                            <Label htmlFor="member2-mobile">Mobile Number</Label>
                            <Input
                              id="member2-mobile"
                              type="tel"
                              placeholder="Enter mobile number"
                              value={olympiadData.member2.mobileNumber}
                              onChange={(e) => {
                                handleOlympiadChange('member2', 'mobileNumber', e.target.value.replace(/\D/g, ''))
                                // Clear error when user types
                                if (validationErrors.member2.mobileNumber) {
                                  setValidationErrors(prev => ({
                                    ...prev,
                                    member2: { ...prev.member2, mobileNumber: '' }
                                  }))
                                }
                              }}
                              onBlur={(e) => {
                                // Skip validation if field is locked
                                if (isFormLocked || fillMember2WithPOC) return

                                // Validate only if field has content
                                if (e.target.value) {
                                  const validation = validatePhone(e.target.value)
                                  if (!validation.isValid) {
                                    setValidationErrors(prev => ({
                                      ...prev,
                                      member2: { ...prev.member2, mobileNumber: validation.error }
                                    }))
                                  }
                                }
                              }}
                              disabled={isFormLocked || fillMember2WithPOC}
                              className={`${validationErrors.member2.mobileNumber && !(isFormLocked || fillMember2WithPOC) ? 'border-red-500' : ''} ${isFormLocked || fillMember2WithPOC ? 'bg-gray-100' : ''}`}
                            />
                            {validationErrors.member2.mobileNumber && !(isFormLocked || fillMember2WithPOC) && (
                              <p className="text-sm text-red-500 mt-1">{validationErrors.member2.mobileNumber}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              Enter mobile number (7-15 digits)
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {!isFormLocked && (
                      <div className="flex justify-end mt-6">
                        <Button
                          onClick={submitOlympiadNominations}
                          disabled={savingOlympiad}
                          className="px-6 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                        >
                          {savingOlympiad ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <Send className="mr-2 h-4 w-4" />
                              Submit Nominations
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Job Posting Modal */}
      {activeModal === 'jobPosting' && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setActiveModal(null)
            }
          }}
        >
          <div
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-r from-blue-50 to-indigo-50 border-b px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-500 text-white rounded-xl">
                  <Briefcase className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold">Fintech Job Posting</h2>
                  <p className="text-sm text-gray-600">Exclusive job posting opportunities</p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
                <p className="text-blue-900">
                  Access exclusive job posting opportunities on the India FinTech platform to attract top talent to your growing company.
                </p>
                {/* <div className="flex items-center gap-2 mt-3 text-blue-700">
                  <Clock className="h-5 w-5" />
                  <span className="font-medium">Due on October 25, 2025</span>
                </div> */}
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  What you'll do:
                </h3>
                <div className="grid gap-3 md:grid-cols-2">
                  {[
                    'Access job posting portal',
                    'Create company job listings',
                    'Review candidate applications',
                    'Conduct interviews and hiring'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <Building2 className="h-4 w-4 text-blue-500" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-center">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                  onClick={() => {
                    console.log('Accessing job portal...')
                    // Add actual navigation/action here
                    window.open('https://jobs.indiafintech.com', '_blank')
                  }}
                >
                  <Briefcase className="mr-2 h-5 w-5" />
                  Access Job Portal
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mixed Data Confirmation Dialog */}
      <Dialog open={showMixedDataDialog} onOpenChange={setShowMixedDataDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <AlertCircle className="h-6 w-6 text-amber-600" />
              Confirm {mixedDataConflicts.member === 'member1' ? 'Team Member 1' : 'Team Member 2'} Details
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-gray-700 mb-4">
              We noticed that the phone or email you entered already exists in our system.
            </p>
            <p className="text-gray-700">
              Please enter the team member's contact details, or confirm if you are the one attending the Fintech Olympiad.
            </p>
          </div>

          <DialogFooter className="flex gap-3">
            <Button
              onClick={handleConfirmEdit}
              variant="outline"
              className="flex-1"
            >
              Edit Details
            </Button>
            <Button
              onClick={handleConfirmYes}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              Yes, I'm Attending
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}